import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { useOps } from '../../hooks/useOps';
import {
  FileStatusTable,
  NotificationProvider,
  UploadButton,
  UploadConfigMissingState,
  UploadEmptyState,
  UploadErrorState,
  UploadLoadingState,
  UploadProvider,
  UploadRefetchingIndicator,
  UploadStats,
  useNotification,
  useUploadContext,
} from './components';
import type { UploadConfig } from './types';

function UploadHeader() {
  const { meta, state, actions } = useUploadContext();
  const { config } = meta;
  const { isRefetching } = state;

  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">{config.title}</h1>
        <p className="text-muted-foreground mt-1">{config.subtitle}</p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={actions.refetch}
        disabled={isRefetching}
        className="flex items-center gap-2"
      >
        <RefreshCw
          className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`}
        />
        Atualizar
      </Button>
    </div>
  );
}

function UploadStatsGrid() {
  const { showSuccess, showError, showWarning } = useNotification();

  const handleUploadSuccess = useCallback(
    (succeeded: string[], failed: string[]) => {
      if (failed.length > 0 && succeeded.length > 0) {
        showWarning(
          `${succeeded.length} arquivo(s) enviado(s), ${failed.length} falha(s)`
        );
      } else if (failed.length > 0) {
        showError(`Falha no upload de ${failed.length} arquivo(s)`);
      } else if (succeeded.length > 0) {
        showSuccess(`${succeeded.length} arquivo(s) enviado(s) com sucesso`);
      }
    },
    [showSuccess, showError, showWarning]
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      showError(error.message);
    },
    [showError]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <UploadButton
        onUploadSuccess={handleUploadSuccess}
        onUploadError={handleUploadError}
      />
      <UploadStats.TotalFiles />
      <UploadStats.ErrorFiles />
      <UploadStats.SuccessFiles />
    </div>
  );
}

function UploadContent() {
  const { state } = useUploadContext();
  const { files, isLoading, error } = state;

  if (isLoading && files.length === 0) {
    return <UploadLoadingState />;
  }

  if (error && files.length === 0) {
    return <UploadErrorState error={error} />;
  }

  if (!isLoading && files.length === 0) {
    return <UploadEmptyState />;
  }

  return <FileStatusTable />;
}

function UploadPageContent() {
  return (
    <div className="p-6">
      <UploadHeader />
      <UploadStatsGrid />
      <UploadContent />
      <UploadRefetchingIndicator />
    </div>
  );
}

interface UploadPageInnerProps {
  config: UploadConfig;
}

function UploadPageInner({ config }: UploadPageInnerProps) {
  const navigate = useNavigate();
  const { currentOps } = useOps();
  const initialOpsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentOps) {
      return;
    }

    if (initialOpsRef.current === null) {
      initialOpsRef.current = currentOps;
      return;
    }

    if (initialOpsRef.current !== currentOps) {
      navigate('/');
    }
  }, [currentOps, navigate]);

  return (
    <NotificationProvider>
      <UploadProvider config={config}>
        <UploadPageContent />
      </UploadProvider>
    </NotificationProvider>
  );
}

export function UploadPage() {
  const location = useLocation();
  const uploadConfig = location.state as UploadConfig | null;

  if (!uploadConfig) {
    return <UploadConfigMissingState />;
  }

  return <UploadPageInner config={uploadConfig} />;
}
